import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';

const TechnicianAssignmentCard = ({ assignment, availableTechnicians, onAssign, onUnassign }) => {
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const technicianOptions = availableTechnicians?.map(tech => ({
    value: tech?.id,
    label: `${tech?.name} - ${tech?.specialization}`,
    description: `${tech?.completedJobs} Aufträge | ${tech?.rating}⭐`
  }));

  const handleAssign = () => {
    if (selectedTechnician) {
      const technician = availableTechnicians?.find(t => t?.id === selectedTechnician);
      onAssign(technician);
      setSelectedTechnician('');
      setIsAssigning(false);
    }
  };

  const handleUnassign = () => {
    onUnassign();
  };

  const getStatusColor = (status) => {
    const colors = {
      AVAILABLE: 'text-success',
      BUSY: 'text-warning',
      OFFLINE: 'text-error'
    };
    return colors?.[status] || 'text-text-secondary';
  };

  const getStatusLabel = (status) => {
    const labels = {
      AVAILABLE: 'Verfügbar',
      BUSY: 'Beschäftigt',
      OFFLINE: 'Offline'
    };
    return labels?.[status] || status;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <Icon name="UserCheck" size={20} />
          Techniker-Zuordnung
        </h3>
        {assignment?.technician && !isAssigning && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAssigning(true)}
            iconName="UserX"
            iconPosition="left"
          >
            Neu zuweisen
          </Button>
        )}
      </div>
      {assignment?.technician && !isAssigning ? (
        <div className="space-y-4">
          {/* Assigned Technician */}
          <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
              <Image
                src={assignment?.technician?.avatar}
                alt={assignment?.technician?.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-text-primary">{assignment?.technician?.name}</h4>
                <span className={`text-sm ${getStatusColor(assignment?.technician?.status)}`}>
                  {getStatusLabel(assignment?.technician?.status)}
                </span>
              </div>
              
              <p className="text-text-secondary text-sm mb-2">{assignment?.technician?.specialization}</p>
              
              <div className="flex items-center gap-4 text-sm text-text-secondary">
                <div className="flex items-center gap-1">
                  <Icon name="CheckCircle" size={14} />
                  <span>{assignment?.technician?.completedJobs} Aufträge</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} />
                  <span>{assignment?.technician?.rating} Bewertung</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Phone" size={14} />
                  <span>{assignment?.technician?.phone}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Assignment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="text-text-secondary">Zugewiesen am</label>
              <div className="text-text-primary">
                {new Date(assignment.assignedAt)?.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            
            <div>
              <label className="text-text-secondary">Geplanter Start</label>
              <div className="text-text-primary">
                {assignment?.scheduledStart ? 
                  new Date(assignment.scheduledStart)?.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 
                  'Nicht geplant'
                }
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`tel:${assignment?.technician?.phone}`, '_self')}
              iconName="Phone"
              iconPosition="left"
            >
              Anrufen
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`sms:${assignment?.technician?.phone}`, '_self')}
              iconName="MessageSquare"
              iconPosition="left"
            >
              SMS
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnassign}
              iconName="UserX"
              iconPosition="left"
            >
              Zuweisung aufheben
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Technician Selection */}
          <Select
            label="Techniker auswählen"
            description="Wählen Sie einen verfügbaren Techniker für diesen Auftrag"
            options={technicianOptions}
            value={selectedTechnician}
            onChange={setSelectedTechnician}
            placeholder="Techniker auswählen..."
            searchable
          />

          {/* Available Technicians Preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-text-secondary">Verfügbare Techniker</label>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {availableTechnicians?.filter(tech => tech?.status === 'AVAILABLE')?.map(tech => (
                <div 
                  key={tech?.id} 
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedTechnician === tech?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => setSelectedTechnician(tech?.id)}
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <Image
                      src={tech?.avatar}
                      alt={tech?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{tech?.name}</div>
                    <div className="text-sm text-text-secondary">{tech?.specialization}</div>
                  </div>
                  
                  <div className="text-right text-sm">
                    <div className="text-text-primary">{tech?.rating}⭐</div>
                    <div className="text-text-secondary">{tech?.completedJobs} Jobs</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="default"
              onClick={handleAssign}
              disabled={!selectedTechnician}
              iconName="UserCheck"
              iconPosition="left"
            >
              Techniker zuweisen
            </Button>
            {isAssigning && (
              <Button
                variant="outline"
                onClick={() => setIsAssigning(false)}
              >
                Abbrechen
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianAssignmentCard;